import { CButton, CCol, CInput, CRow } from "@coreui/react";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import "./ImageUploader.scss";
import {
  defaultDivision,
  getDivisionPrimaryButtonClass,
} from "../../../utils/formatUtils";

export default function AttachmentUploader({
  onUploadImageClick,
  accept,
  existingImageCount = 0,
  validateFileOnChange = undefined,
  preprocessFilesBeforeSave,
  uploadSuccessMessage = "File upload successful.",
  uploadErrorMessage = "There was a problem uploading this file.",
  noFilesErrorMessage = "No file selected.",
}: {
  onUploadImageClick: (fd: FormData) => Promise<any>;
  existingImageCount: number;
  accept?: string;
  validateFileOnChange?: (file: File) => Promise<File>;
  preprocessFilesBeforeSave?: (file: File) => Promise<File>;
  uploadSuccessMessage?: string;
  uploadErrorMessage?: string;
  noFilesErrorMessage?: string;
}) {
  const [imagesToSave, setImagesToSave] = useState<File[]>([]);
  const uploaderElement = useRef<HTMLInputElement>(undefined);

  const maxImageCount = 20;

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const imageFiles = e.target.files;

      if (validateFileOnChange) {
        const imageArray = await Promise.all(
          Array.from(imageFiles).map(validateFileOnChange),
        );
        setImagesToSave(imageArray);
      } else {
        setImagesToSave(Array.from(imageFiles));
      }
    } else {
      setImagesToSave([]);
      clearUploaderValue(uploaderElement.current);
    }
  };

  const validate = () => {
    const temp = {
      imagesToSave: imagesToSave ? true : false,
    };

    return (
      imagesToSave.length > 0 && Object.values(temp).every((x) => x === true)
    );
  };

  const handleSaveImage = async (e: React.MouseEvent<HTMLElement>) => {
    if (existingImageCount + (imagesToSave?.length || 0) > maxImageCount) {
      toast.error(
        `You may only upload a total of ${maxImageCount} images. Please delete some existing images or reduce your selection.`,
      );
    } else {
      const isValid = validate();
      if (isValid) {
        try {
          const compressedFiles = await Promise.all(
            imagesToSave.map((file) => {
              if (preprocessFilesBeforeSave) {
                return preprocessFilesBeforeSave(file);
              } else {
                return file;
              }
            }),
          );
          const formData = new FormData();
          compressedFiles.forEach((compressedFile) => {
            formData.append("imageName", compressedFile.name);
            formData.append("imageFile", compressedFile);
          });

          await onUploadImageClick(formData)
            .then(() => {
              toast.success(uploadSuccessMessage);
            })
            .catch((err) => {
              toast.error(uploadErrorMessage);
            });
        } catch (error) {
          console.log(error);
        }

        setImagesToSave([]);
        clearUploaderValue(uploaderElement.current);
      } else {
        toast.error(noFilesErrorMessage);
      }
    }
  };

  return (
    <>
      <CRow className="w-100">
        <CCol className="pr-0">
          <CInput
            className="h-100 m-0 p-0"
            type="file"
            id="image-uploader"
            accept={accept}
            onChange={handleImageChange}
            multiple
            innerRef={uploaderElement}
          ></CInput>
        </CCol>

        <CButton
          type="button"
          onClick={handleSaveImage}
          className={
            getDivisionPrimaryButtonClass(defaultDivision) + " ml-3 mr-0"
          }
        >
          Upload
        </CButton>
      </CRow>
    </>
  );
}

function clearUploaderValue(el?: HTMLInputElement) {
  if (el) {
    el.value = "";
  }
}
